import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { UserCog, Crown, Edit2, Plus, X, Mail, Phone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function InternalAdviserAssignment({ clientId, client, onUpdated }) {
  const { token } = useAuth();
  const [advisers, setAdvisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [draftLead, setDraftLead] = useState('');
  const [draftSupporting, setDraftSupporting] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchAdvisers = useCallback(async () => {
    try {
      const t = token || localStorage.getItem('qindex_token');
      if (!t) { setLoading(false); return; }
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/internal-advisers?activeOnly=true`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${t}` } });
      const data = r.ok ? await r.json() : { advisers: [] };
      setAdvisers(data?.advisers || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAdvisers(); }, [fetchAdvisers]);

  const advById = (id) => advisers.find(a => a.id === id);
  const lead = advById(client?.leadAdviserId);
  const supporting = (client?.supportingAdviserIds || []).map(advById).filter(Boolean);

  const openModal = () => {
    setDraftLead(client?.leadAdviserId || '');
    setDraftSupporting(client?.supportingAdviserIds || []);
    setShowModal(true);
  };

  const toggleSupporting = (id) => {
    setDraftSupporting(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(token, `/clients/${clientId}/internal-advisers`, {
        leadAdviserId: draftLead || null,
        supportingAdviserIds: draftSupporting.filter(id => id !== draftLead),
      });
      toast.success('Internal advisers updated');
      setShowModal(false);
      onUpdated?.();
    } catch (err) {
      toast.error('Failed to update');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="caffeine-card p-6" data-testid="internal-adviser-assignment">
        <div className="flex items-center gap-2 text-[#8C8078]">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading internal advisers...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="caffeine-card p-6" data-testid="internal-adviser-assignment">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <UserCog className="w-5 h-5 text-[#A86A3A]" />
          <h3 className="font-heading text-lg text-[#2C2420]">Internal Team</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={openModal}
          className="gap-1 text-xs text-[#5D524D]"
          data-testid="internal-adviser-edit-btn"
        >
          {lead || supporting.length > 0 ? <><Edit2 className="w-3 h-3" /> Edit</> : <><Plus className="w-3 h-3" /> Assign</>}
        </Button>
      </div>

      {advisers.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-sm text-[#8C8078]">No internal advisers in your hub yet.</p>
          <p className="text-xs text-[#A8A09A] mt-1">Add advisers from the Internal Adviser Hub to assign them.</p>
        </div>
      ) : !lead && supporting.length === 0 ? (
        <div className="text-center py-4 border border-dashed border-[#E6E0D9] rounded-lg">
          <p className="text-sm text-[#8C8078]">No advisers assigned yet</p>
          <Button onClick={openModal} variant="outline" size="sm" className="mt-2 text-xs" data-testid="internal-adviser-assign-empty-btn">
            <Plus className="w-3 h-3 mr-1" /> Assign team
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Lead Adviser */}
          {lead && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F5F0EA] border border-[#E6D9C6]" data-testid="lead-adviser-card">
              <div className="w-10 h-10 rounded-full bg-[#A86A3A] text-white flex items-center justify-center font-medium shrink-0">
                {lead.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-medium text-[#2C2420] truncate">{lead.name}</p>
                  <Crown className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" />
                  <Badge className="bg-[#A86A3A]/10 text-[#A86A3A] text-[10px]">Lead</Badge>
                </div>
                <p className="text-xs text-[#8C8078]">{lead.title}</p>
                <div className="flex items-center gap-3 mt-0.5 text-[10px] text-[#5D524D]">
                  {lead.email && <a href={`mailto:${lead.email}`} className="flex items-center gap-0.5 hover:text-[#A86A3A]"><Mail className="w-2.5 h-2.5" />Email</a>}
                  {lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center gap-0.5 hover:text-[#A86A3A]"><Phone className="w-2.5 h-2.5" />Call</a>}
                </div>
              </div>
            </div>
          )}

          {/* Supporting Advisers */}
          {supporting.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-[#8C8078] mb-2">Supporting Advisers</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {supporting.map(adv => (
                  <div key={adv.id} className="flex items-center gap-2 p-2 rounded-lg border border-[#E6E0D9]" data-testid={`supporting-adviser-${adv.id}`}>
                    <div className="w-8 h-8 rounded-full bg-[#4A3B32] text-white flex items-center justify-center text-xs font-medium shrink-0">
                      {adv.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#2C2420] truncate">{adv.name}</p>
                      <p className="text-[10px] text-[#8C8078] truncate">{adv.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md" data-testid="internal-adviser-modal">
          <DialogHeader>
            <DialogTitle>Assign Internal Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#2C2420] flex items-center gap-1.5">
                <Crown className="w-3.5 h-3.5 text-amber-500" /> Lead Adviser
              </label>
              <Select value={draftLead || '__none__'} onValueChange={(v) => setDraftLead(v === '__none__' ? '' : v)}>
                <SelectTrigger data-testid="lead-adviser-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No lead adviser —</SelectItem>
                  {advisers.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}{a.title ? ` · ${a.title}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#2C2420]">Supporting Advisers</label>
              <p className="text-xs text-[#8C8078]">Click to toggle. The lead is automatically excluded.</p>
              <div className="flex flex-wrap gap-2 pt-1">
                {advisers.filter(a => a.id !== draftLead).length === 0 && (
                  <p className="text-xs text-[#8C8078]">No other advisers available.</p>
                )}
                {advisers.filter(a => a.id !== draftLead).map(adv => {
                  const on = draftSupporting.includes(adv.id);
                  return (
                    <button
                      key={adv.id}
                      type="button"
                      onClick={() => toggleSupporting(adv.id)}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-all flex items-center gap-1 ${
                        on ? 'bg-[#4A3B32] text-white border-[#4A3B32]' : 'bg-white text-[#5D524D] border-[#E6E0D9] hover:border-[#4A3B32]'
                      }`}
                      data-testid={`supporting-toggle-${adv.id}`}
                    >
                      {on && <X className="w-3 h-3" />}
                      {adv.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#4A3B32] text-white hover:bg-[#3a2e26]" data-testid="internal-adviser-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
